import { sha256Hex } from "../integration/local-ifc";
import { canonicalJson } from "./d2-property-proposal-candidate";

/**
 * Builds the exact wire request `POST /projects/:id/models/:model_id/property-proposals` expects,
 * and computes `proposal_digest` using BIM-Hub's own formula -- both read from, not guessed at, the
 * contract BIM-Hub published after landing the receiver in production:
 * `bim-hub-integration/answers/2026-08-13-property-proposal-connection-go.md` §4 (itself quoting
 * `orchestrator-integration/reports/2026-08-13-property-proposal-receiver-production-complete.md`
 * §4 in the sefket-editor-development workspace verbatim). This module builds the payload only --
 * no `fetch`, no transport -- mirroring `d3-commit-payload.ts`'s own "stop short of the network
 * boundary" split between payload-building and `src/server/bim-hub-client.ts`'s actual POST.
 *
 * `canonicalJson` (lexicographic object keys, no insignificant whitespace, native `JSON.stringify`
 * number formatting) is reused as-is from `d2-property-proposal-candidate.ts` -- the same shared
 * convention `d3-commit-payload.ts` already reuses for its own commit-payload digest, and the same
 * one the contract's "RFC 8785-like canonical JSON, ECMAScript number representation" describes:
 * ECMAScript number representation is exactly `JSON.stringify`'s own number-to-string algorithm,
 * which is what `JSON.stringify` already does once object keys are pre-sorted.
 */

export type PropertyPrimitiveType = "string" | "number" | "boolean";

export interface PropertyPrimitiveValue {
  readonly type: PropertyPrimitiveType;
  readonly value: string | number | boolean;
}

export interface PropertyProposalInput {
  readonly projectId: string;
  readonly modelId: string;
  readonly baseRevision: number;
  readonly sourceDocumentId: string | null;
  readonly occurrenceGuid: string;
  readonly propertyPath: string;
  readonly before: PropertyPrimitiveValue;
  readonly after: PropertyPrimitiveValue;
  readonly clientCapabilityVersion?: string;
}

export const PROPERTY_PROPOSAL_SCHEMA = "bim-hub.property-proposal/1" as const;

/**
 * The exact wire shape §4.2/§4.5 defines. Field ORDER here is irrelevant to the digest (the digest
 * is computed separately, over a specific 7-value array below -- not over this whole object) but
 * matters for readability/debuggability of what actually goes over the wire.
 */
export interface PropertyProposalRequest {
  readonly schema: typeof PROPERTY_PROPOSAL_SCHEMA;
  readonly project_id: string;
  readonly model_id: string;
  readonly base_revision: number;
  readonly source_document_id: string | null;
  readonly occurrence: { readonly guid: string };
  readonly property_path: string;
  readonly before: PropertyPrimitiveValue;
  readonly after: PropertyPrimitiveValue;
  readonly proposal_digest: string;
  readonly client_capability_version?: string;
}

function assertPrimitiveTypeMatches(before: PropertyPrimitiveValue, after: PropertyPrimitiveValue): void {
  if (before.type !== after.type) {
    throw new Error(`Property proposal before/after type mismatch: ${before.type} vs ${after.type}.`);
  }
}

function assertNonEmpty(name: string, value: string): void {
  if (value.trim() === "") throw new Error(`Property proposal ${name} must not be empty.`);
}

/**
 * §4.2's `property_path` rule: "フラット、ドット/配列添字/`guid`/`id`不可" -- flat (no `.`/`[`),
 * and never literally `guid`/`id` (the receiver's own identity-spoofing guard, §3 of the production
 * report, rejects those with a 409 `property_proposal_not_writable`). Checked here too so a caller
 * gets an immediate, specific local error instead of a round-trip to learn the same thing.
 */
function assertValidPropertyPath(propertyPath: string): void {
  assertNonEmpty("propertyPath", propertyPath);
  if (propertyPath.includes(".") || propertyPath.includes("[")) {
    throw new Error(`Property proposal propertyPath must be flat (no "." or "["): ${propertyPath}`);
  }
  if (propertyPath === "guid" || propertyPath === "id") {
    throw new Error(`Property proposal propertyPath must not be "guid" or "id": ${propertyPath}`);
  }
}

function assertValidBaseRevision(baseRevision: number): void {
  if (!Number.isSafeInteger(baseRevision) || baseRevision <= 0) {
    throw new Error("Property proposal baseRevision must be a positive safe integer.");
  }
}

/**
 * §4.2's exact digest formula:
 * `sha256("[" + canonical(project_id, model_id, base_revision, occurrence.guid,
 *                          property_path, before, after) + "]")`
 * -- read as: canonically encode the 7-element JSON ARRAY `[project_id, model_id, base_revision,
 * occurrence.guid, property_path, before, after]` (a JSON array's own `[`/`]` delimiters ARE that
 * "[" + ... + "]" wrapping; `before`/`after` stay as their wire-shape `{type, value}` objects,
 * object keys canonically sorted). `unknown_fields` are never part of this array, matching §4.2's
 * "`unknown_fields`は対象外" and the production report's own named test #10
 * (`the_digest_computation_excludes_unknown_fields`).
 */
export async function computeProposalDigest(
  input: Pick<PropertyProposalInput, "projectId" | "modelId" | "baseRevision" | "occurrenceGuid" | "propertyPath" | "before" | "after">
): Promise<string> {
  const canonicalArray = canonicalJson([
    input.projectId,
    input.modelId,
    input.baseRevision,
    input.occurrenceGuid,
    input.propertyPath,
    input.before,
    input.after
  ]);
  const hex = await sha256Hex(new TextEncoder().encode(canonicalArray));
  return `sha256:${hex}`;
}

/**
 * Builds and digests a full request in one call. Throws (does not return a typed refusal) on any
 * locally-checkable contract violation -- type mismatch, empty ids, malformed property_path, or a
 * non-positive base_revision -- the same "fail fast before a network round-trip" discipline as
 * `d3-commit-payload.ts`'s own `assertBaseRevision`.
 */
export async function buildPropertyProposalRequest(input: PropertyProposalInput): Promise<PropertyProposalRequest> {
  assertNonEmpty("projectId", input.projectId);
  assertNonEmpty("modelId", input.modelId);
  assertNonEmpty("occurrenceGuid", input.occurrenceGuid);
  assertValidPropertyPath(input.propertyPath);
  assertValidBaseRevision(input.baseRevision);
  assertPrimitiveTypeMatches(input.before, input.after);

  const proposalDigest = await computeProposalDigest(input);
  return {
    schema: PROPERTY_PROPOSAL_SCHEMA,
    project_id: input.projectId,
    model_id: input.modelId,
    base_revision: input.baseRevision,
    source_document_id: input.sourceDocumentId,
    occurrence: { guid: input.occurrenceGuid },
    property_path: input.propertyPath,
    before: input.before,
    after: input.after,
    proposal_digest: proposalDigest,
    ...(input.clientCapabilityVersion === undefined ? {} : { client_capability_version: input.clientCapabilityVersion })
  };
}

const SHA256_DIGEST = /^sha256:[0-9a-f]{64}$/;

function isPrimitiveValue(value: unknown): value is PropertyPrimitiveValue {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (candidate.type === "string" && typeof candidate.value === "string") ||
    (candidate.type === "number" && typeof candidate.value === "number") ||
    (candidate.type === "boolean" && typeof candidate.value === "boolean")
  );
}

/**
 * Validates an arbitrary `unknown` JSON body IS a well-formed `PropertyProposalRequest` -- the
 * browser builds the request (via `buildPropertyProposalRequest`, which already digests it) and
 * POSTs the finished object to this app's own BFF; this is the BFF's own defense-in-depth check
 * before it ever forwards the body to BIM-Hub, mirroring `d3-save.ts`'s `parseD3SaveRequestBody`.
 * Does NOT recompute or verify `proposal_digest` -- BIM-Hub's own receiver already does that
 * server-side (§4.4: a mismatch is a 400) and is the actual authority; re-verifying here would only
 * duplicate that check with a second, potentially drifting implementation.
 */
export function parsePropertyProposalRequest(value: unknown): PropertyProposalRequest | null {
  if (typeof value !== "object" || value === null) return null;
  const candidate = value as Record<string, unknown>;
  const occurrence = candidate.occurrence;
  if (
    candidate.schema !== PROPERTY_PROPOSAL_SCHEMA ||
    typeof candidate.project_id !== "string" || candidate.project_id.trim() === "" ||
    typeof candidate.model_id !== "string" || candidate.model_id.trim() === "" ||
    typeof candidate.base_revision !== "number" || !Number.isSafeInteger(candidate.base_revision) || candidate.base_revision <= 0 ||
    (candidate.source_document_id !== null && typeof candidate.source_document_id !== "string") ||
    typeof occurrence !== "object" || occurrence === null || typeof (occurrence as Record<string, unknown>).guid !== "string" ||
    typeof candidate.property_path !== "string" || candidate.property_path.trim() === "" ||
    !isPrimitiveValue(candidate.before) ||
    !isPrimitiveValue(candidate.after) ||
    typeof candidate.proposal_digest !== "string" || !SHA256_DIGEST.test(candidate.proposal_digest) ||
    (candidate.client_capability_version !== undefined && typeof candidate.client_capability_version !== "string")
  ) {
    return null;
  }
  return {
    schema: PROPERTY_PROPOSAL_SCHEMA,
    project_id: candidate.project_id,
    model_id: candidate.model_id,
    base_revision: candidate.base_revision,
    source_document_id: candidate.source_document_id as string | null,
    occurrence: { guid: (occurrence as { guid: string }).guid },
    property_path: candidate.property_path,
    before: candidate.before as PropertyPrimitiveValue,
    after: candidate.after as PropertyPrimitiveValue,
    proposal_digest: candidate.proposal_digest,
    ...(candidate.client_capability_version === undefined ? {} : { client_capability_version: candidate.client_capability_version as string })
  };
}
